import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import nodeAxiosFirebase from '../utils/nodeAxiosFirebase';
import { isAutomationDevelopmentHost } from '../utils/featureFlags';
import { isSimpleAutomation } from '../utils/automationEditorPolicy';
import {
  toUnifiedModel,
  fromUnifiedModel,
  getAutomationChanges,
  cloneAutomationValue,
  canBindAutomationStructure,
} from '../utils/automationMapper';

// nodeAxiosFirebase returns { errorData } for many failures instead of throwing.
// Never treat those responses as a successful load or a successful write.
function requireAutomationResponse(response, fallback) {
  if (!response || response.errorData || response.success === false || response.error) {
    const detail = response?.errorData?.data?.message || response?.errorData?.message || response?.message;
    const error = new Error(typeof detail === 'string' ? detail : fallback);
    error.code = response?.errorData?.data?.error || response?.errorData?.code || response?.error;
    error.status = response?.errorData?.status;
    throw error;
  }
  return response;
}

const automationMessage = (t, key, fallback) => t(key, { defaultValue: fallback });

const advancedEditorMessage = (t) => automationMessage(t, 'automationAdvancedDevOnly',
  'Les fonctions avancées sont disponibles uniquement sur dev.usenode.com. Cet automatisme est conservé sans modification.');

// Legacy raw editors cannot safely identify a simple automation from a partial
// payload. Keep them on development hosts; the guided editor uses saveAutomation.
function requireAdvancedEditor(t) {
  if (!isAutomationDevelopmentHost()) throw new Error(advancedEditorMessage(t));
}

function requirePreservingUpdate(data, t, workflow = false) {
  if (!data?.expectedEditorRevision ||
      (workflow && Object.prototype.hasOwnProperty.call(data, 'steps') && data.preserveStepShape !== true)) {
    throw new Error(automationMessage(t, 'automationUsePreservingEditor',
      'Rechargez cet automatisme dans l’éditeur pour conserver son schéma et vérifier sa version avant de le modifier.'));
  }
}

// Async thunks for workflows
export const fetchWorkflows = createAsyncThunk(
  'automation/fetchWorkflows',
  async ({ structureId, isActive, t }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (structureId) params.append('structureId', structureId);
      if (isActive !== undefined) params.append('isActive', isActive);

      const response = await nodeAxiosFirebase({
        t,
        method: 'GET',
        url: `bosV4/workflows?${params.toString()}`,
        errorToast: t('errorLoadingWorkflows'),
      });

      return response.workflows || [];
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createWorkflow = createAsyncThunk(
  'automation/createWorkflow',
  async ({ workflowData, t }, { rejectWithValue }) => {
    try {
      requireAdvancedEditor(t);
      const response = await nodeAxiosFirebase({
        t,
        method: 'POST',
        url: 'bosV4/workflows',
        body: workflowData,
        errorToast: t('errorCreatingWorkflow'),
      });

      return response.workflow;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateWorkflow = createAsyncThunk(
  'automation/updateWorkflow',
  async ({ workflowId, workflowData, t }, { rejectWithValue }) => {
    try {
      requireAdvancedEditor(t);
      requirePreservingUpdate(workflowData, t, true);
      const response = await nodeAxiosFirebase({
        t,
        method: 'PUT',
        url: `bosV4/workflows/${workflowId}`,
        body: workflowData,
        errorToast: t('errorUpdatingWorkflow'),
      });

      return { workflowId, workflow: response.workflow };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteWorkflow = createAsyncThunk(
  'automation/deleteWorkflow',
  async ({ workflowId, t }, { rejectWithValue }) => {
    try {
      requireAdvancedEditor(t);
      await nodeAxiosFirebase({
        t,
        method: 'DELETE',
        url: `bosV4/workflows/${workflowId}`,
        errorToast: t('errorDeletingWorkflow'),
      });

      return workflowId;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getWorkflowDetails = createAsyncThunk(
  'automation/getWorkflowDetails',
  async ({ workflowId, t }, { rejectWithValue }) => {
    try {
      const response = await nodeAxiosFirebase({
        t,
        method: 'GET',
        url: `bosV4/workflows/${workflowId}`,
        errorToast: t('errorLoadingWorkflow'),
      });

      return response.workflow;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const executeWorkflow = createAsyncThunk(
  'automation/executeWorkflow',
  async ({ workflowId, elementData, t }, { rejectWithValue }) => {
    try {
      const response = await nodeAxiosFirebase({
        t,
        method: 'POST',
        url: `bosV4/workflows/${workflowId}/execute`,
        body: { elementData },
        errorToast: t('errorExecutingWorkflow'),
      });

      return response.result;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Async thunks for field triggers
export const fetchFieldTriggers = createAsyncThunk(
  'automation/fetchFieldTriggers',
  async ({ elementId, isActive, t }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (elementId) params.append('elementId', elementId);
      if (isActive !== undefined) params.append('isActive', isActive);

      const response = await nodeAxiosFirebase({
        t,
        method: 'GET',
        url: `main/field-triggers?${params.toString()}`,
        errorToast: t('errorLoadingTriggers'),
      });

      return response.triggers || [];
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createFieldTrigger = createAsyncThunk(
  'automation/createFieldTrigger',
  async ({ triggerData, t }, { rejectWithValue }) => {
    try {
      requireAdvancedEditor(t);
      const response = await nodeAxiosFirebase({
        t,
        method: 'POST',
        url: 'main/field-triggers',
        body: triggerData,
        errorToast: t('errorCreatingTrigger'),
      });

      return response.trigger;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateFieldTrigger = createAsyncThunk(
  'automation/updateFieldTrigger',
  async ({ triggerId, triggerData, t }, { rejectWithValue }) => {
    try {
      requireAdvancedEditor(t);
      requirePreservingUpdate(triggerData, t);
      const response = await nodeAxiosFirebase({
        t,
        method: 'PUT',
        url: `main/field-triggers/${triggerId}`,
        body: triggerData,
        errorToast: t('errorUpdatingTrigger'),
      });

      return { triggerId, trigger: response.trigger };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteFieldTrigger = createAsyncThunk(
  'automation/deleteFieldTrigger',
  async ({ triggerId, t }, { rejectWithValue }) => {
    try {
      requireAdvancedEditor(t);
      await nodeAxiosFirebase({
        t,
        method: 'DELETE',
        url: `main/field-triggers/${triggerId}`,
        errorToast: t('errorDeletingTrigger'),
      });

      return triggerId;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Unified automation thunks (combines trigger + workflow)
export const loadAutomation = createAsyncThunk(
  'automation/loadAutomation',
  async ({ trigger, t }, { rejectWithValue }) => {
    try {
      if (!trigger?.id) throw new Error(t('errorLoadingTriggers'));
      // List rows are projections; never edit or persist their missing fields.
      const detail = requireAutomationResponse(await nodeAxiosFirebase({
        t, method: 'GET',
        url: `main/field-triggers?elementId=${encodeURIComponent(trigger.id)}`,
        errorToast: t('errorLoadingTriggers'),
      }), t('errorLoadingTriggers'));
      const completeTrigger = detail.triggers?.find((item) => item.id === trigger.id);
      if (!completeTrigger) throw new Error(t('errorLoadingTriggers'));
      let workflow = null;
      let workflowResponse = null;
      if (completeTrigger.workflowId) {
        workflowResponse = requireAutomationResponse(await nodeAxiosFirebase({
          t,
          method: 'GET',
          url: `bosV4/workflows/${encodeURIComponent(completeTrigger.workflowId)}`,
          errorToast: t('errorLoadingWorkflow'),
        }), t('errorLoadingWorkflow'));
        workflow = workflowResponse.workflow;
        if (!workflow) throw new Error(t('errorLoadingWorkflow'));
      }

      return toUnifiedModel(completeTrigger, workflow, {
        ...workflowResponse?.editorCapabilities,
        workflowRevision: workflowResponse?.editorRevision,
        triggerRevision: detail.editorRevision,
        triggerOptimisticRevision: detail.editorCapabilities?.optimisticRevision === true,
      });
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const saveAutomation = createAsyncThunk(
  'automation/saveAutomation',
  async ({ unified, mode, t }, { rejectWithValue, dispatch }) => {
    let recovery = null;
    try {
      if (mode === 'edit' && !unified._source) {
        throw new Error(t('errorLoadingWorkflow'));
      }
      const { triggerData, workflowData } = fromUnifiedModel(unified);
      const { triggerPatch, workflowPatch, hasChanges } = getAutomationChanges(unified);
      const progress = unified._saveProgress;
      let workflowId = progress?.workflowId || unified.workflowId;
      let triggerId = unified.triggerId;
      if (mode === 'edit' && !hasChanges) return { triggerId, workflowId, mode, unchanged: true };

      // Check the full draft and its opening snapshots, never a filtered step
      // array. Removing an advanced step cannot turn a legacy workflow editable.
      if (!isAutomationDevelopmentHost() && !isSimpleAutomation(unified)) {
        throw new Error(advancedEditorMessage(t));
      }

      const capabilities = unified._source?.capabilities || {};
      const workflowRevision = progress?.workflowRevision || capabilities.workflowRevision;
      const workflowChanged = Object.keys(workflowPatch).length > 0;
      const triggerChanged = Object.keys(triggerPatch).length > 0;
      const unsupportedMessage = automationMessage(t, 'automationEditorServerUpdateRequired',
        'Le serveur doit être mis à jour pour enregistrer cette modification sans transformer le schéma existant.',
        'The server must be updated before this change can be saved without transforming the existing schema.');

      // Preflight both writes before changing either document. An older server
      // ignores unknown flags and normalizes steps; never send it legacy data.
      if (unified._source) {
        if (workflowChanged && (!capabilities.optimisticRevision || !workflowRevision ||
            ('steps' in workflowPatch && !capabilities.preserveStepShape))) {
          throw new Error(unsupportedMessage);
        }
        if (triggerChanged && (!capabilities.triggerOptimisticRevision || !capabilities.triggerRevision)) {
          throw new Error(unsupportedMessage);
        }
        if (('structureId' in workflowPatch || 'structureId' in triggerPatch) &&
            !canBindAutomationStructure(unified)) {
          throw new Error(automationMessage(t, 'automationStructureChangeRequiresNewAutomation',
            'Créez un nouvel automatisme pour changer de structure; la structure de cet automatisme est conservée.',
            'Create a new automation to use a different structure; this automation keeps its original structure.'));
        }
        // Workflow triggerType is historical metadata; its PUT API does not
        // update it. Only the explicitly edited field-trigger event is sent.
        delete workflowPatch.triggerType;
      }

      if (unified._source && ('structureId' in workflowPatch || 'structureId' in triggerPatch)) {
        if (!unified.structureId || !workflowId || !triggerId || 'workflowId' in triggerPatch) {
          throw new Error(t('structureRequired'));
        }
        delete workflowPatch.structureId;
        delete triggerPatch.structureId;
        // The first binding and any accompanying edits commit as a single unit.
        // An uncertain result must be reloaded; no partial-save retry is created.
        requireAutomationResponse(await nodeAxiosFirebase({
          t, method: 'PUT', url: `bosV4/workflows/${workflowId}`,
          body: { ...workflowPatch, preserveStepShape: true, expectedEditorRevision: workflowRevision,
            bindStructure: { structureId: unified.structureId, triggerId,
              expectedTriggerRevision: capabilities.triggerRevision, triggerPatch } },
          errorToast: t('errorUpdatingWorkflow'),
        }), t('errorUpdatingWorkflow'));
        try {
          await dispatch(fetchFieldTriggers({ t, silent: true })).unwrap();
        } catch (refreshError) {
          console.warn('Failed to refresh automations list:', refreshError);
        }
        return { triggerId, workflowId, mode };
      }

      let nextRevision = workflowRevision;
      if (!workflowId) {
        const wfResponse = requireAutomationResponse(await nodeAxiosFirebase({
          t,
          method: 'POST',
          url: 'bosV4/workflows',
          body: workflowData,
          errorToast: t('errorCreatingWorkflow'),
        }), t('errorCreatingWorkflow'));
        workflowId = wfResponse?.workflow?.id || wfResponse?.id;
        if (!workflowId) throw new Error(t('errorCreatingWorkflow'));
        nextRevision = wfResponse.editorRevision;
        recovery = { ...cloneAutomationValue(unified), _saveProgress: {
          workflowId, workflowData: cloneAutomationValue(workflowData), workflowRevision: nextRevision,
        } };
      } else if (Object.keys(workflowPatch).length > 0) {
        if (progress && !nextRevision) throw new Error(unsupportedMessage);
        const wfResponse = requireAutomationResponse(await nodeAxiosFirebase({
          t,
          method: 'PUT',
          url: `bosV4/workflows/${workflowId}`,
          body: { ...workflowPatch, preserveStepShape: true,
            ...(nextRevision ? { expectedEditorRevision: nextRevision } : {}) },
          errorToast: t('errorUpdatingWorkflow'),
        }), t('errorUpdatingWorkflow'));
        nextRevision = wfResponse.editorRevision;
        recovery = { ...cloneAutomationValue(unified), _saveProgress: {
          workflowId, workflowData: cloneAutomationValue(workflowData), workflowRevision: nextRevision,
        } };
      }

      if (!triggerId) {
        const tResponse = requireAutomationResponse(await nodeAxiosFirebase({
          t,
          method: 'POST',
          url: 'main/field-triggers',
          body: { ...triggerData, workflowId },
          errorToast: t('errorCreatingTrigger'),
        }), t('errorCreatingTrigger'));
        triggerId = tResponse?.trigger?.id || tResponse?.id;
        if (!triggerId) throw new Error(t('errorCreatingTrigger'));
      } else if (triggerChanged) {
        requireAutomationResponse(await nodeAxiosFirebase({
          t,
          method: 'PUT',
          url: `main/field-triggers/${triggerId}`,
          body: { ...triggerPatch, expectedEditorRevision: capabilities.triggerRevision },
          errorToast: t('errorUpdatingTrigger'),
        }), t('errorUpdatingTrigger'));
      }

      try {
        await dispatch(fetchFieldTriggers({ t, silent: true })).unwrap();
      } catch (refreshError) {
        console.warn('Failed to refresh automations list:', refreshError);
      }

      return { triggerId, workflowId, mode };
    } catch (error) {
      const conflict = error.code === 'AUTOMATION_EDIT_CONFLICT' || error.status === 409 || error.response?.status === 409;
      return rejectWithValue({
        message: conflict
          ? t('automationChangedElsewhere', { defaultValue: 'Cet automatisme a été modifié ailleurs. Rechargez la version enregistrée avant de sauvegarder.' })
          : error.response?.data?.message || error.message,
        ...(conflict ? { conflict: true } : {}),
        ...(recovery ? { recovery, partiallySaved: true } : {}),
      });
    }
  }
);

// Async thunk for workflow logs
export const fetchWorkflowLogs = createAsyncThunk(
  'automation/fetchWorkflowLogs',
  async ({ workflowId, elementId, limit = 50, t }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (workflowId) params.append('workflowId', workflowId);
      if (elementId) params.append('elementId', elementId);
      if (limit) params.append('limit', limit);

      const response = await nodeAxiosFirebase({
        t,
        method: 'GET',
        url: `bosV4/workflow-logs?${params.toString()}`,
        errorToast: t('errorLoadingLogs'),
      });

      return response.logs || [];
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Async thunk for single workflow log detail
export const fetchWorkflowLogDetail = createAsyncThunk(
  'automation/fetchWorkflowLogDetail',
  async ({ logId, t }, { rejectWithValue }) => {
    try {
      const response = await nodeAxiosFirebase({
        t,
        method: 'GET',
        url: `bosV4/workflow-log-detail?logId=${logId}`,
        errorToast: t('errorLoadingLogs'),
      });

      return response.log || null;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  workflows: {
    data: [],
    loading: false,
    error: null,
    selectedWorkflow: null,
  },
  fieldTriggers: {
    data: [],
    loading: false,
    error: null,
    selectedTrigger: null,
  },
  logs: {
    data: [],
    loading: false,
    error: null,
    selectedLogDetail: null,
    logDetailLoading: false,
    logDetailError: null,
  },
  execution: {
    loading: false,
    error: null,
    result: null,
  },
  unified: {
    loading: false,
    saving: false,
    error: null,
    current: null,
  },
  filters: {
    structureId: null,
    isActive: null,
  },
};

const automationSlice = createSlice({
  name: 'automation',
  initialState,
  reducers: {
    setSelectedWorkflow: (state, action) => {
      state.workflows.selectedWorkflow = action.payload;
    },
    setSelectedTrigger: (state, action) => {
      state.fieldTriggers.selectedTrigger = action.payload;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearExecutionResult: (state) => {
      state.execution.result = null;
      state.execution.error = null;
    },
    clearErrors: (state) => {
      state.workflows.error = null;
      state.fieldTriggers.error = null;
      state.logs.error = null;
      state.execution.error = null;
    },
    clearLogDetail: (state) => {
      state.logs.selectedLogDetail = null;
      state.logs.logDetailError = null;
    },
  },
  extraReducers: (builder) => {
    // Workflows
    builder
      .addCase(fetchWorkflows.pending, (state) => {
        state.workflows.loading = true;
        state.workflows.error = null;
      })
      .addCase(fetchWorkflows.fulfilled, (state, action) => {
        state.workflows.loading = false;
        state.workflows.data = action.payload;
      })
      .addCase(fetchWorkflows.rejected, (state, action) => {
        state.workflows.loading = false;
        state.workflows.error = action.payload;
      })
      .addCase(createWorkflow.fulfilled, (state, action) => {
        state.workflows.data.push(action.payload);
      })
      .addCase(updateWorkflow.fulfilled, (state, action) => {
        const index = state.workflows.data.findIndex(
          (w) => w.id === action.payload.workflowId
        );
        if (index !== -1) {
          state.workflows.data[index] = action.payload.workflow;
        }
      })
      .addCase(deleteWorkflow.fulfilled, (state, action) => {
        state.workflows.data = state.workflows.data.filter(
          (w) => w.id !== action.payload
        );
      })
      .addCase(getWorkflowDetails.fulfilled, (state, action) => {
        state.workflows.selectedWorkflow = action.payload;
      });

    // Field Triggers
    builder
      .addCase(fetchFieldTriggers.pending, (state, action) => {
        if (!action.meta.arg?.silent) {
          state.fieldTriggers.loading = true;
        }
        state.fieldTriggers.error = null;
      })
      .addCase(fetchFieldTriggers.fulfilled, (state, action) => {
        state.fieldTriggers.loading = false;
        state.fieldTriggers.data = action.payload;
      })
      .addCase(fetchFieldTriggers.rejected, (state, action) => {
        state.fieldTriggers.loading = false;
        state.fieldTriggers.error = action.payload;
      })
      .addCase(createFieldTrigger.fulfilled, (state, action) => {
        state.fieldTriggers.data.push(action.payload);
      })
      .addCase(updateFieldTrigger.fulfilled, (state, action) => {
        const index = state.fieldTriggers.data.findIndex(
          (t) => t.id === action.payload.triggerId
        );
        if (index !== -1) {
          state.fieldTriggers.data[index] = action.payload.trigger;
        }
      })
      .addCase(deleteFieldTrigger.fulfilled, (state, action) => {
        state.fieldTriggers.data = state.fieldTriggers.data.filter(
          (t) => t.id !== action.payload
        );
      });

    // Logs
    builder
      .addCase(fetchWorkflowLogs.pending, (state) => {
        state.logs.loading = true;
        state.logs.error = null;
      })
      .addCase(fetchWorkflowLogs.fulfilled, (state, action) => {
        state.logs.loading = false;
        state.logs.data = action.payload;
      })
      .addCase(fetchWorkflowLogs.rejected, (state, action) => {
        state.logs.loading = false;
        state.logs.error = action.payload;
      })
      .addCase(fetchWorkflowLogDetail.pending, (state) => {
        state.logs.logDetailLoading = true;
        state.logs.logDetailError = null;
      })
      .addCase(fetchWorkflowLogDetail.fulfilled, (state, action) => {
        state.logs.logDetailLoading = false;
        state.logs.selectedLogDetail = action.payload;
      })
      .addCase(fetchWorkflowLogDetail.rejected, (state, action) => {
        state.logs.logDetailLoading = false;
        state.logs.logDetailError = action.payload;
      });

    // Execution
    builder
      .addCase(executeWorkflow.pending, (state) => {
        state.execution.loading = true;
        state.execution.error = null;
      })
      .addCase(executeWorkflow.fulfilled, (state, action) => {
        state.execution.loading = false;
        state.execution.result = action.payload;
      })
      .addCase(executeWorkflow.rejected, (state, action) => {
        state.execution.loading = false;
        state.execution.error = action.payload;
      });

    // Unified automation thunks
    builder
      .addCase(loadAutomation.pending, (state) => {
        state.unified.loading = true;
        state.unified.error = null;
      })
      .addCase(loadAutomation.fulfilled, (state, action) => {
        state.unified.loading = false;
        state.unified.current = action.payload;
      })
      .addCase(loadAutomation.rejected, (state, action) => {
        state.unified.loading = false;
        state.unified.error = action.payload;
      })
      .addCase(saveAutomation.pending, (state) => {
        state.unified.saving = true;
        state.unified.error = null;
      })
      .addCase(saveAutomation.fulfilled, (state) => {
        state.unified.saving = false;
        state.unified.current = null;
      })
      .addCase(saveAutomation.rejected, (state, action) => {
        state.unified.saving = false;
        state.unified.error = action.payload;
      });
  },
});

export const {
  setSelectedWorkflow,
  setSelectedTrigger,
  setFilters,
  clearExecutionResult,
  clearErrors,
  clearLogDetail,
} = automationSlice.actions;

export default automationSlice.reducer;
